import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RetailerAttributeDefinitionDetailsResponse, AttributeDefinitionRequest, RetailerAttributeDefinitionsService } from '@src/app/shared/backend/clients';
import { AttributeDefinitionFormData } from '@src/app/shared/components/attribute-definition-form/attribute-definition.model';

@Injectable({
  providedIn: 'root'
})
export class RetailerAttributeDefinitionsAdapter {

  constructor(private retailerAttributeDefinitionsService: RetailerAttributeDefinitionsService) {}

  async get(id: number): Promise<AttributeDefinitionFormData> {
    const response = await firstValueFrom(this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsDetailsIdGet(id));
    return this.mapResponseToFormData(response);
  }

  async create(formData: AttributeDefinitionFormData): Promise<void> {
    const request = this.mapFormDataToRequest(formData);

    await firstValueFrom(this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsCreatePost(request));
  }

  async update(id: number, formData: AttributeDefinitionFormData): Promise<void> {
    const request = this.mapFormDataToRequest(formData);

    await firstValueFrom(this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsUpdateIdPut(id, request));
  }

  private mapResponseToFormData(response: RetailerAttributeDefinitionDetailsResponse): AttributeDefinitionFormData {
    const translations = response.retailerAttributeDefinitionTranslations?.map(translation => ({
      isoCode: translation.isoCode,
      translatedName: translation.translatedName
    })) ?? [];

    const values = response.retailerAttributeValues?.map(value => {
      const valueTranslations = value.retailerAttributeValueTranslations?.map(translation => ({
        isoCode: translation.isoCode,
        translatedValue: translation.translatedValue
      })) ?? [];

      return {
        valueCode: value.valueCode,
        isActive: value.isActive,
        translations: valueTranslations
      };
    }) ?? [];

    return {
      definitionCode: response.definitionCode,
      dataType: response.retailerAttributeDataType,
      allowMultipleValues: response.allowMultipleValues,
      changesOverTime: response.changesOverTime,
      translations,
      values
    };
  }

  private mapFormDataToRequest(formData: AttributeDefinitionFormData): AttributeDefinitionRequest {

    const attributeDefinitionTranslations = formData.translations.map(translation => ({
      isoCode: translation.isoCode,
      translatedName: translation.translatedName
    }));

    const retailerAttributeValues = formData.values.map(value => {
      const attributeValueTranslations = value.translations.map(translation => ({
        isoCode: translation.isoCode,
        translatedValue: translation.translatedValue
      }));

      return {
        valueCode: value.valueCode,
        isActive: value.isActive,
        attributeValueTranslations: attributeValueTranslations
      };
    });

    return {
      definitionCode: formData.definitionCode,
      attributeDataType: formData.dataType,
      allowMultipleValues: formData.allowMultipleValues,
      changesOverTime: formData.changesOverTime,
      attributeDefinitionTranslations: attributeDefinitionTranslations,
      attributeValues: retailerAttributeValues
    };
  }
}
